import { Instagram, MessageCircle, Phone, Send } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * §v3 — channel identifier. The kit uses a small colored tile with a
 * dark glyph inside; the color carries identity at a glance, the glyph
 * confirms which channel when the colors are too close to read.
 *
 * Channels supported: telegram / whatsapp / voice / desktop / phone.
 * Phone and voice render the same glyph (it's the same channel
 * spelled two ways in the unified_history schema). Desktop falls
 * back to the chat glyph in voice color.
 *
 * Size:
 *   xs — 14px tile, 8px glyph. Used as an avatar-corner indicator.
 *   sm — 22px tile, 12px glyph. Used inline next to a name.
 *   md — 28px tile, 16px glyph. Used standalone in lists.
 */

export type ChannelKey =
  | "telegram"
  | "whatsapp"
  | "instagram"
  | "voice"
  | "openai_phone"
  | "phone"
  | "gemini_desktop"
  | "desktop"
  | "test-chat"
  | string;

export type ChannelDotSize = "xs" | "sm" | "md";

const SIZE_TILE: Record<ChannelDotSize, string> = {
  xs: "h-3.5 w-3.5",
  sm: "h-5.5 w-5.5",
  md: "h-7 w-7",
};

const SIZE_GLYPH: Record<ChannelDotSize, string> = {
  xs: "h-2 w-2",
  sm: "h-3 w-3",
  md: "h-4 w-4",
};

// §v3.sip-observability — "voice-sip" and "voice-web-test" join
// the voice family (Phone glyph + voice colour) so they render
// identically to the existing phone channels in every list.
const VOICE_CHANNELS = new Set<string>([
  "voice",
  "openai_phone",
  "phone",
  "voice-sip",
  "voice-web-test",
]);

function colorVar(channel: ChannelKey): string {
  if (channel === "telegram" || channel === "test-chat") return "var(--channel-telegram)";
  if (channel === "whatsapp") return "var(--channel-whatsapp)";
  // §v3.instagram (2026-05-25) — Instagram brand pink. Literal (no token yet);
  // reads cleanly behind the dark glyph like the other channel tiles.
  if (channel === "instagram") return "#E1306C";
  if (VOICE_CHANNELS.has(channel)) return "var(--channel-voice)";
  // Desktop / gemini_desktop / unknown channels share the voice slot
  // (a neutral grey); the glyph carries the identity if needed.
  return "var(--channel-voice)";
}

function GlyphFor({
  channel,
  className,
}: {
  channel: ChannelKey;
  className: string;
}) {
  if (VOICE_CHANNELS.has(channel))
    return <Phone className={className} aria-hidden strokeWidth={1.75} />;
  if (channel === "telegram" || channel === "test-chat")
    return <Send className={className} aria-hidden strokeWidth={1.75} />;
  if (channel === "instagram")
    return <Instagram className={className} aria-hidden strokeWidth={1.75} />;
  return <MessageCircle className={className} aria-hidden strokeWidth={1.75} />;
}

export function ChannelDot({
  channel,
  size = "sm",
  className,
}: {
  channel: ChannelKey;
  size?: ChannelDotSize;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-grid place-items-center rounded-full",
        SIZE_TILE[size],
        className,
      )}
      style={{
        background: colorVar(channel),
        // Black-tinted glyph on a mid-luminance fill keeps contrast
        // legible without going pure-white (which would clash with
        // the dark canvas underneath when the glass is translucent).
        color: "rgba(13, 14, 20, 0.85)",
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.25), 0 1px 2px rgba(0,0,0,0.3)",
      }}
      aria-label={String(channel)}
    >
      <GlyphFor channel={channel} className={SIZE_GLYPH[size]} />
    </span>
  );
}
